import React from 'react';
import { cssColorValue, semanticColors, spacer16, spacer32, spacer8, Text } from '@spotify-internal/encore-web';
import styled from 'styled-components';

import PageContainer from 'components/PageContainer';
import IconChevron from 'components/IconChevron';
import { QuickLink } from 'lib/sanity/types';
import { useTranslation } from 'providers/translations/TranslationProvider';
import { ArticleLink } from 'components/Links';
import List from 'components/List';
import { UbiSearch } from 'utils/ubi/types';

const Container = styled.div`
  background-color: ${cssColorValue(semanticColors.backgroundElevatedHighlight)};
`;

const StyledIconChevron = styled(IconChevron)`
  align-self: center; // Align in the middle of multi-line links
  margin-inline-start: ${spacer8};
  color: ${cssColorValue(semanticColors.textBase)};
`;

const StyledArticleLink = styled(ArticleLink)`
  display: flex;
  justify-content: space-between;
  padding: ${spacer16} 0;
`;

interface QuickLinksProps {
  quickLinks: QuickLink[];
  ubi: UbiSearch;
}

const QuickLinks = ({ quickLinks, ubi }: QuickLinksProps) => {
  const { t } = useTranslation();

  if (!quickLinks.length) return null;

  // This quickLinks in search only renders on homepage, and the ubi for it only exists on the homepage spec
  const logQuickLink = ubi.logQuickLink!;

  return (
    <Container>
      <PageContainer data-testid="quickLinksContainer">
        <Text as="h2" variant="titleSmall" paddingBottom={spacer32}>
          {t('quick_help')}
        </Text>
        <List>
          {quickLinks.map((quickLink, index) => (
            <li key={quickLink.slug}>
              <StyledArticleLink
                slug={quickLink.slug}
                eventDefinition={{
                  category: 'top_solutions',
                  label: 'home_page',
                  action: quickLink.slug,
                }}
                standalone
                ubiFactory={logQuickLink(quickLink.slug, index)}
              >
                <Text variant="bodyMedium" semanticColor="textBase">
                  {quickLink.title}
                </Text>
                <StyledIconChevron size={16} />
              </StyledArticleLink>
            </li>
          ))}
        </List>
      </PageContainer>
    </Container>
  );
};

export default QuickLinks;
